import React, { useEffect } from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
} from '@/components/ui/card';
import { Link, useNavigate } from 'react-router-dom';

const Services = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title =
      'Startup Fundraising Services | FounderScale Capital';

    const metaDescription =
      'FounderScale Capital provides startup fundraising services including investor discovery, investor outreach, pitch decks, financial modeling, data rooms, and fundraising strategy.';

    let meta = document.querySelector(
      'meta[name="description"]'
    );

    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }

    meta.setAttribute('content', metaDescription);
  }, []);

  const services = [
    {
      title: 'Model 1: Basic Investor Outreach',
      price: 'Starting at $2,500',
      description:
        'Targeted investor outreach for founders who already have their core fundraising materials prepared.',
      features: [
        '50–100 targeted investor connections',
        'Investor segmentation and targeting',
        'Email and LinkedIn outreach drafting',
        'Outreach scheduling',
        'Follow-up tracker',
        'Investor pipeline dashboard',
        '30 days of active outreach',
      ],
    },
    {
      title: 'Model 2: Premium Package',
      price: 'Starting at $7,500',
      description:
        'Comprehensive fundraising support covering investor preparation, positioning, outreach, and meeting preparation.',
      features: [
        'Business and traction review',
        'Pitch deck improvement',
        'Fundraising narrative development',
        'Raise amount and use-of-funds planning',
        'Investor FAQs',
        'Basic data room preparation',
        'Investor meeting preparation',
        '60 days of fundraising support',
      ],
    },
    {
      title: 'Model 3: Full-Service Support',
      price: 'Custom Pricing',
      description:
        'End-to-end fundraising support for founders who want structured assistance throughout the fundraising process.',
      features: [
        'Dedicated relationship manager',
        'Analyst meeting',
        'Warm investor introductions',
        'Investor negotiation guidance',
        'Due diligence documentation support',
        'Term sheet review',
        'Closing support',
        'Post-funding advisory',
        '90+ days of support',
      ],
    },
  ];

  const additionalServices = [
    {
      title: 'Pitch Deck Creation from Scratch',
      description:
        'Build an investor-focused pitch deck that clearly communicates your business, market opportunity, traction, business model, and fundraising requirement.',
    },
    {
      title: 'Financial Model Development',
      description:
        'Develop structured financial projections to help founders understand their business economics and present financial information to investors.',
    },
    {
      title: 'Business Plan Writing',
      description:
        'Create a structured business plan covering the business model, market opportunity, strategy, operations, and growth plans.',
    },
    {
      title: 'Market Research and Analysis',
      description:
        'Research market size, trends, customer segments, industry dynamics, and other relevant information needed for investor discussions.',
    },
    {
      title: 'Competitive Landscape Mapping',
      description:
        'Map relevant competitors and market participants to help founders understand positioning, differentiation, and competitive dynamics.',
    },
    {
      title: 'Investor Presentation Training',
      description:
        'Prepare founders for investor conversations by helping them structure their presentation, communicate key metrics, and handle investor questions.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-emerald-950 to-gray-950 text-white">

      {/* Hero Section */}
      <section className="relative py-24 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/20 to-green-900/20" />

        <div className="relative max-w-5xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Startup Fundraising{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-300 to-emerald-200">
              Services
            </span>
          </h1>

          <p className="text-xl text-green-100 mb-8 leading-relaxed max-w-3xl mx-auto">
            From investment readiness and investor discovery to targeted
            outreach and fundraising execution, FounderScale Capital helps
            founders build a structured path from pitch to capital.
          </p>

          <Button
            onClick={() => navigate('/contact')}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg"
          >
            Discuss Your Fundraising
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Main Service Models */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">

          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-white mb-4">
              Choose the Right Fundraising Support
            </h2>

            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Our fundraising support can be structured around your startup's
              stage, preparation level, fundraising requirements, and investor
              outreach needs.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card
                key={index}
                className="bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-300"
              >
                <CardHeader>
                  <h3 className="text-2xl font-bold text-white mb-3">
                    {service.title}
                  </h3>

                  <div className="text-2xl font-bold text-emerald-300 mb-4">
                    {service.price}
                  </div>

                  <p className="text-gray-300 leading-relaxed">
                    {service.description}
                  </p>
                </CardHeader>

                <CardContent>
                  <div className="space-y-4">
                    {service.features.map((feature, featureIndex) => (
                      <div
                        key={featureIndex}
                        className="flex items-start gap-3"
                      >
                        <div className="mt-1 flex-shrink-0">
                          <Check className="h-5 w-5 text-emerald-400" />
                        </div>

                        <span className="text-gray-300">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={() => navigate('/contact')}
                    className="w-full mt-8 bg-emerald-500 hover:bg-emerald-600 text-white"
                  >
                    Get Started
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Fundraising Support Section */}
      <section className="py-20 px-6 bg-black/20">
        <div className="max-w-6xl mx-auto">

          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-white mb-5">
              Fundraising Support Built Around Your Startup's Needs
            </h2>

            <p className="text-lg text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Successful fundraising involves more than sending investor
              emails. Founders need a clear story, credible financial
              information, investor-ready documentation, targeted investor
              research, and a structured outreach process.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-10">

            {/* Investment Readiness */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-6">
                <span className="text-emerald-300 text-xl font-bold">
                  01
                </span>
              </div>

              <h3 className="text-2xl font-bold text-white mb-4">
                Investment Readiness
              </h3>

              <p className="text-gray-300 leading-relaxed mb-5">
                Before approaching investors, founders need to clearly
                communicate their business, market opportunity, traction,
                financial requirements, and growth plans. We help structure
                the information investors are likely to review during the
                fundraising process.
              </p>

              <p className="text-gray-300 leading-relaxed mb-5">
                This can include pitch deck preparation, financial modeling,
                fundraising requirements, use-of-funds planning, investor
                FAQs, and data room preparation.
              </p>

              <Link
                to="/services/pitch-deck"
                className="inline-flex items-center text-emerald-300 hover:text-emerald-200 font-medium"
              >
                Explore Pitch Deck Services
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>

            {/* Investor Discovery */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-6">
                <span className="text-emerald-300 text-xl font-bold">
                  02
                </span>
              </div>

              <h3 className="text-2xl font-bold text-white mb-4">
                Investor Discovery & Mapping
              </h3>

              <p className="text-gray-300 leading-relaxed mb-5">
                Investor targeting should be based on the startup's stage,
                sector, geography, business model, fundraising requirement,
                and the investor's investment mandate.
              </p>

              <p className="text-gray-300 leading-relaxed mb-5">
                We help founders identify and organize relevant investor
                segments, including angels, HNIs, family offices, micro VCs,
                venture capital firms, and other potential sources of capital.
              </p>

              <Link
                to="/services/investor-discovery"
                className="inline-flex items-center text-emerald-300 hover:text-emerald-200 font-medium"
              >
                Explore Investor Discovery
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>

            {/* Investor Outreach */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-6">
                <span className="text-emerald-300 text-xl font-bold">
                  03
                </span>
              </div>

              <h3 className="text-2xl font-bold text-white mb-4">
                Investor Outreach
              </h3>

              <p className="text-gray-300 leading-relaxed mb-5">
                Once relevant investors have been identified, outreach needs
                to be organized and personalized. We support founders with
                investor communication, outreach sequencing, follow-ups, and
                pipeline organization.
              </p>

              <p className="text-gray-300 leading-relaxed mb-5">
                The objective is to create a structured process that allows
                founders to track investor conversations and maintain
                visibility across the fundraising pipeline.
              </p>

              <Link
                to="/services/investor-outreach"
                className="inline-flex items-center text-emerald-300 hover:text-emerald-200 font-medium"
              >
                Explore Investor Outreach
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>

            {/* Fundraising Execution */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mb-6">
                <span className="text-emerald-300 text-xl font-bold">
                  04
                </span>
              </div>

              <h3 className="text-2xl font-bold text-white mb-4">
                Fundraising Execution
              </h3>

              <p className="text-gray-300 leading-relaxed mb-5">
                Fundraising continues after the initial investor introduction.
                Founders may need support preparing for investor meetings,
                responding to questions, organizing diligence information,
                and managing the fundraising process.
              </p>

              <p className="text-gray-300 leading-relaxed mb-5">
                Our broader support can include fundraising strategy, meeting
                preparation, data room support, investor communication, and
                guidance through the later stages of the process.
              </p>

              <Link
                to="/services/fundraising-assistance"
                className="inline-flex items-center text-emerald-300 hover:text-emerald-200 font-medium"
              >
                Explore Fundraising Assistance
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>

          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">

          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-white mb-5">
              Additional Startup Fundraising Services
            </h2>

            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Founders can also access focused support for specific
              fundraising documents, research requirements, and investor
              preparation needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">

            {additionalServices.map((service, index) => (
              <Card
                key={index}
                className="bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-300"
              >
                <CardContent className="p-7">

                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center mb-5">
                    <Check className="h-5 w-5 text-emerald-400" />
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3">
                    {service.title}
                  </h3>

                  <p className="text-gray-300 leading-relaxed">
                    {service.description}
                  </p>

                </CardContent>
              </Card>
            ))}

          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/contact')}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6"
            >
              Discuss Your Needs
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>

        </div>
      </section>

      {/* Internal Service Links */}
      <section className="py-16 px-6 bg-black/20">
        <div className="max-w-5xl mx-auto">

          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-4">
              Explore Our Fundraising Services
            </h2>

            <p className="text-gray-300">
              Explore individual services based on the specific requirements
              of your fundraising process.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">

            <Link
              to="/services/fundraising-assistance"
              className="group bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-all"
            >
              <div className="flex items-center justify-between">
                <span className="text-white font-medium">
                  Fundraising Assistance
                </span>
                <ArrowRight className="h-4 w-4 text-emerald-300 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/services/investor-discovery"
              className="group bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-all"
            >
              <div className="flex items-center justify-between">
                <span className="text-white font-medium">
                  Investor Discovery
                </span>
                <ArrowRight className="h-4 w-4 text-emerald-300 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/services/investor-outreach"
              className="group bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-all"
            >
              <div className="flex items-center justify-between">
                <span className="text-white font-medium">
                  Investor Outreach
                </span>
                <ArrowRight className="h-4 w-4 text-emerald-300 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/services/pitch-deck"
              className="group bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-all"
            >
              <div className="flex items-center justify-between">
                <span className="text-white font-medium">
                  Pitch Deck Creation
                </span>
                <ArrowRight className="h-4 w-4 text-emerald-300 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/services/financial-modeling"
              className="group bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-all"
            >
              <div className="flex items-center justify-between">
                <span className="text-white font-medium">
                  Financial Modeling
                </span>
                <ArrowRight className="h-4 w-4 text-emerald-300 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/services/data-room"
              className="group bg-white/5 border border-white/10 rounded-xl p-5 hover:bg-white/10 transition-all"
            >
              <div className="flex items-center justify-between">
                <span className="text-white font-medium">
                  Data Room Creation
                </span>
                <ArrowRight className="h-4 w-4 text-emerald-300 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">

          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Not Sure Which Fundraising Support You Need?
          </h2>

          <p className="text-xl text-gray-300 mb-10 leading-relaxed">
            Every startup is different. Connect with FounderScale Capital to
            discuss your business, fundraising stage, investor requirements,
            and the type of support you may need.
          </p>

          <Button
            onClick={() => navigate('/contact')}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-10 py-6 text-lg"
          >
            Connect With Us
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>

        </div>
      </section>

    </div>
  );
};

export default Services;
